import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'react-bootstrap'
import BoxOverlay from './BoxOverlay'
import { angleTowards, moveBox, newBox, resizeFromCorner, rotateBox } from './box-geometry'
import { detectBoxes } from './detect-boxes'

const MAX_DISPLAY_WIDTH = 760

// One page shown large, where the user adds a box per receipt and lines each one up with its receipt.
const PageBoxEditor = ({ page, pageSize, boxes, noun, labelFor, colorFor, onChange, renderPageView, renderPreview, onDone }) => {
  const containerRef = useRef(null)
  const overlayRef = useRef(null)
  const drag = useRef(null)
  const [displayWidth, setDisplayWidth] = useState(MAX_DISPLAY_WIDTH)
  const [selectedId, setSelectedId] = useState(boxes[0]?.id || null)
  const [finding, setFinding] = useState(false)
  const [findResult, setFindResult] = useState(null)

  useEffect(() => {
    const measure = () => setDisplayWidth(Math.min(MAX_DISPLAY_WIDTH, containerRef.current?.clientWidth || MAX_DISPLAY_WIDTH))
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])

  const scale = pageSize ? displayWidth / pageSize.width : 1
  const selected = boxes.find((box) => box.id === selectedId)

  const pagePoint = (event) => {
    const rect = overlayRef.current.getBoundingClientRect()
    return { x: (event.clientX - rect.left) / scale, y: (event.clientY - rect.top) / scale }
  }

  const replace = (updated) => onChange(boxes.map((box) => (box.id === updated.id ? updated : box)))

  const handlePress = (event, box, mode, corner) => {
    setSelectedId(box.id)
    overlayRef.current.setPointerCapture?.(event.pointerId)
    drag.current = { mode, corner, start: pagePoint(event), box }
  }

  const handleMove = (event) => {
    const current = drag.current
    if (!current) return
    const point = pagePoint(event)
    if (current.mode === 'move') replace(moveBox(current.box, point.x - current.start.x, point.y - current.start.y))
    if (current.mode === 'resize') replace(resizeFromCorner(current.box, current.corner, point))
    if (current.mode === 'rotate') replace({ ...current.box, angle: angleTowards(current.box, point) })
  }

  const endDrag = () => {
    drag.current = null
  }

  const addBox = () => {
    const box = newBox(page, pageSize, boxes.length)
    onChange([...boxes, box])
    setSelectedId(box.id)
  }

  // Suggestions land as ordinary boxes; any that would sit on a box already drawn are left out.
  const findBoxes = async () => {
    setFinding(true)
    setFindResult(null)
    try {
      const { canvas, scale: previewScale } = await renderPreview(page)
      const image = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height)
      const covered = (candidate) =>
        boxes.some((box) => Math.abs(candidate.cx - box.cx) < box.w / 2 && Math.abs(candidate.cy - box.cy) < box.h / 2)
      const detected = detectBoxes(image, previewScale)
      const found = detected
        .filter((candidate) => !covered(candidate))
        .map((candidate, i) => ({ ...candidate, id: `${page}-found-${Date.now()}-${i}` }))
      onChange([...boxes, ...found])
      setFindResult({ added: found.length, detected: detected.length })
    } catch (error) {
      setFindResult({ added: 0, detected: 0 })
    } finally {
      setFinding(false)
    }
  }

  const removeSelected = () => {
    onChange(boxes.filter((box) => box.id !== selectedId))
    setSelectedId(null)
  }

  return (
    <div ref={containerRef}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Button variant="primary" size="sm" onClick={addBox} disabled={!pageSize} data-testid="splitter-add-box">
          Add {noun}
        </Button>
        <Button
          variant="light"
          className="text-primary"
          size="sm"
          onClick={findBoxes}
          disabled={!pageSize || finding}
          data-testid="splitter-find-boxes"
        >
          {finding ? 'Looking…' : `Find ${noun}s`}
        </Button>
        {selected && (
          <>
            <Button variant="light" className="text-primary" size="sm" onClick={() => replace(rotateBox(selected, -90))}>
              Turn left
            </Button>
            <Button variant="light" className="text-primary" size="sm" onClick={() => replace(rotateBox(selected, 90))}>
              Turn right
            </Button>
            <span className="small text-muted" data-testid="splitter-box-angle">{selected.angle}°</span>
            <Button variant="light" className="text-danger" size="sm" onClick={removeSelected}>
              Remove {labelFor(selected)}
            </Button>
          </>
        )}
        <Button variant="light" className="text-primary ms-auto" size="sm" onClick={onDone} data-testid="splitter-done-boxes">
          Back to all pages
        </Button>
      </div>
      {findResult !== null && (
        <p className="small mb-2" data-testid="splitter-find-result">
          {findResult.added > 0 &&
            `Added ${findResult.added} box${findResult.added === 1 ? '' : 'es'}. Check each one lines up with its ${noun}, and turn any that came out upside down.`}
          {findResult.added === 0 && findResult.detected > 0 && `Every ${noun} found already has a box.`}
          {findResult.detected === 0 && `No ${noun}s stood out from the background. Add boxes yourself.`}
        </p>
      )}
      <p className="small text-muted mb-2">
        Add a box for each {noun} on page {page}, then drag it over the {noun}. Drag a corner to resize it and the
        round handle to turn it until it lines up. Only what is inside the boxes is uploaded.
      </p>
      <div className="position-relative d-inline-block border" style={{ width: displayWidth, maxWidth: '100%' }}>
        {renderPageView(displayWidth)}
        <div
          ref={overlayRef}
          className="position-absolute top-0 start-0 w-100 h-100"
          style={{ touchAction: 'none' }}
          onPointerMove={handleMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerDown={() => setSelectedId(null)}
          data-testid="splitter-page-editor"
        >
          {pageSize && (
            <BoxOverlay
              boxes={boxes}
              scale={scale}
              labelFor={labelFor}
              colorFor={colorFor}
              selectedId={selectedId}
              onPress={handlePress}
            />
          )}
        </div>
      </div>
    </div>
  )
}

PageBoxEditor.propTypes = {
  page: PropTypes.number.isRequired,
  pageSize: PropTypes.shape({ width: PropTypes.number, height: PropTypes.number }),
  boxes: PropTypes.arrayOf(PropTypes.object).isRequired,
  noun: PropTypes.string.isRequired,
  labelFor: PropTypes.func.isRequired,
  colorFor: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  renderPageView: PropTypes.func.isRequired,
  renderPreview: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired
}

export default PageBoxEditor
